import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileDown, ReceiptText, RefreshCw, Search } from "lucide-react";
import { api } from "../../lib/api";
import { useDebouncedValue } from "../../lib/utils";
import {
  AdminPageHeader,
  Button,
  Callout,
  Input,
  Metric,
  Panel,
  Select,
  StateChip,
  errorMessage,
  formatDate,
  formatMoney,
  humanize,
  type TransactionRow,
} from "./client-operations-shared";

export function TransactionsWorkspace() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [currency, setCurrency] = useState("");
  const [search, setSearch] = useState("");
  const [billedFrom, setBilledFrom] = useState("");
  const [billedTo, setBilledTo] = useState("");
  const [includeCanceled, setIncludeCanceled] = useState(false);
  const debounced = useDebouncedValue(search.trim(), 250);
  const transactionsQ = useInfiniteQuery({
    queryKey: ["admin", "transactions", status, kind, currency, debounced, billedFrom, billedTo, includeCanceled],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "50" });
      if (status) params.set("status", status);
      if (kind) params.set("kind", kind);
      if (currency) params.set("currency", currency);
      if (debounced) params.set("search", debounced);
      if (billedFrom) params.set("billedFrom", billedFrom);
      if (billedTo) params.set("billedTo", billedTo);
      if (includeCanceled) params.set("includeCanceled", "true");
      if (pageParam) params.set("cursor", pageParam);
      return api<{
        transactions: TransactionRow[];
        nextCursor: string | null;
        paddleDashboardUrl: string | null;
      }>(`/admin/transactions?${params}`);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  const resyncM = useMutation({
    mutationFn: () => api<{
      attempted: number;
      succeeded: Array<{ companyId: string; paddleSubscriptionId: string }>;
      failed: Array<{ companyId: string; paddleSubscriptionId: string; code: string }>;
    }>("/admin/billing/resync-all", {
      method: "POST",
      body: JSON.stringify({ acknowledgement: "resync_all_from_paddle" }),
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "transactions"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "inbox"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "billing", "health"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "client"] }),
      ]);
    },
  });
  const rows = transactionsQ.data?.pages.flatMap((page) => page.transactions) ?? [];
  const paddleDashboardUrl = transactionsQ.data?.pages[0]?.paddleDashboardUrl ?? null;
  const due = rows.filter((row) => ["billed", "past_due"].includes(row.status) && !row.paidAt);
  const overdue = due.filter((row) => row.status === "past_due" || (row.dueAt && new Date(row.dueAt).getTime() < Date.now()));
  const paidByCurrency = totalsByCurrency(rows.filter((row) => ["paid", "completed"].includes(row.status)));

  return (
    <div className="space-y-5">
      <AdminPageHeader
        eyebrow="Paddle mirror"
        title="Transactions"
        description="Only server-attributed Cascena records appear. Search and filters run against the mirror; canceled records stay preserved but are hidden by default."
        actions={<Button disabled={resyncM.isPending} onClick={() => resyncM.mutate()}><RefreshCw size={13} /> {resyncM.isPending ? "Resyncing all…" : "Resync all from Paddle"}</Button>}
      />
      {resyncM.data && <Callout tone={resyncM.data.failed.length ? "warning" : "success"} title="Paddle resync finished">{resyncM.data.succeeded.length} of {resyncM.data.attempted} subscriptions and their transactions converged through the shared mapper.{resyncM.data.failed.length ? ` ${resyncM.data.failed.length} need individual review.` : ""}</Callout>}
      {resyncM.isError && <Callout tone="danger" title="Account-wide resync did not start">{errorMessage(resyncM.error)}</Callout>}
      <div className="grid gap-3 sm:grid-cols-3">
        <Metric label="Unpaid" value={due.length} tone={due.length ? "warning" : "neutral"} detail="Billed and not yet paid" />
        <Metric label="Overdue" value={overdue.length} tone={overdue.length ? "danger" : "neutral"} detail="Our due-date clock" />
        <Metric label="Paid in view" value={paidByCurrency || "—"} tone="good" detail="Never blended across currencies" />
      </div>
      <Panel>
        <div className="ops-toolbar ops-admin-filter-bar ops-admin-transaction-filters grid gap-2 border-b border-border p-3">
          <label className="relative"><Search size={14} className="pointer-events-none absolute left-2.5 top-2.5 text-muted" /><Input className="pl-8" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Client, invoice, or transaction ID" aria-label="Search transactions" /></label>
          <Select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter transaction status"><option value="">All active statuses</option>{["ready", "billed", "past_due", "paid", "completed", "canceled"].map((value) => <option key={value} value={value}>{humanize(value)}</option>)}</Select>
          <Select value={kind} onChange={(event) => setKind(event.target.value)} aria-label="Filter transaction kind"><option value="">All kinds</option>{["initial_onboarding", "subscription", "one_time_overage", "final_true_up", "adjustment"].map((value) => <option key={value} value={value}>{humanize(value)}</option>)}</Select>
          <Select value={currency} onChange={(event) => setCurrency(event.target.value)} aria-label="Filter currency"><option value="">All currencies</option>{["GBP", "EUR", "USD", "RON", "CHF"].map((value) => <option key={value} value={value}>{value}</option>)}</Select>
          <Input type="date" value={billedFrom} onChange={(event) => setBilledFrom(event.target.value)} aria-label="Billed from" title="Billed from" />
          <Input type="date" value={billedTo} onChange={(event) => setBilledTo(event.target.value)} aria-label="Billed to" title="Billed to" />
          <label className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-md border border-border px-3 text-xs"><input type="checkbox" checked={includeCanceled} onChange={(event) => setIncludeCanceled(event.target.checked)} /> Include canceled</label>
        </div>
        {transactionsQ.isLoading ? (
          <div className="p-8 text-center text-sm text-muted">Loading transactions…</div>
        ) : transactionsQ.isError ? (
          <div className="p-4"><Callout tone="danger" title="Transactions could not be loaded">{errorMessage(transactionsQ.error)}</Callout></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center"><ReceiptText size={24} className="mx-auto text-muted" /><div className="mt-2 text-sm font-medium">No attributed transactions in this view</div></div>
        ) : (
          <div className="ops-admin-table-scroll overflow-x-auto">
            <table className="w-full min-w-[1180px] text-left text-xs">
              <thead className="border-b border-border bg-border/10 text-[11px] uppercase tracking-wide text-muted"><tr><th className="p-3 font-medium">Client</th><th className="p-3 font-medium">Invoice / transaction</th><th className="p-3 font-medium">Kind</th><th className="p-3 font-medium">Collection</th><th className="p-3 font-medium">Status</th><th className="p-3 text-right font-medium">Subtotal</th><th className="p-3 text-right font-medium">Tax</th><th className="p-3 text-right font-medium">Total</th><th className="p-3 font-medium">Billed / due / paid</th><th className="p-3 text-right font-medium">Actions</th></tr></thead>
              <tbody>{rows.map((row) => (
                <tr key={row.paddleTransactionId} className="border-b border-border/50 last:border-0 hover:bg-border/10">
                  <td className="p-3">{row.companyId ? <Link to="/admin/clients/$clientId" params={{ clientId: row.companyId }} className="font-medium hover:text-accent">{row.companyName ?? "Unnamed client"}</Link> : <span className="text-negRed">Unattributed</span>}</td>
                  <td className="p-3"><div className="font-medium">{row.invoiceNumber ?? "Prepared transaction"}</div><div className="mt-0.5 font-mono text-[10px] text-muted">{row.paddleTransactionId}</div></td>
                  <td className="p-3">{humanize(row.kind ?? "transaction")}</td>
                  <td className="p-3"><div>{humanize(row.collectionMode ?? "unknown")}</div>{row.collectionMode === "manual" && <div className="mt-0.5 text-[10px] text-muted">Checkout {row.checkoutEnabled ? "enabled" : "disabled"} · Net {row.paymentTermsDays ?? "—"}</div>}</td>
                  <td className="p-3"><StateChip value={row.status} /></td>
                  <td className="p-3 text-right tabular-nums">{formatMoney(row.subtotalMinor, row.currency)}</td>
                  <td className="p-3 text-right tabular-nums">{formatMoney(row.taxMinor, row.currency)}</td>
                  <td className="p-3 text-right font-medium tabular-nums">{formatMoney(row.totalMinor, row.currency)}</td>
                  <td className="p-3 text-[11px]"><div>B {formatDate(row.billedAt)}</div><div>D {formatDate(row.dueAt)}</div><div>P {formatDate(row.paidAt)}</div></td>
                  <td className="p-3 text-right"><div className="inline-flex gap-1">{row.companyId && <Link to="/admin/clients/$clientId" params={{ clientId: row.companyId }}><Button variant="quiet" title="Open client"><ExternalLink size={13} /></Button></Link>}{paddleDashboardUrl && <Button variant="quiet" title="Copy transaction ID and open Paddle" onClick={() => openPaddleTransaction(paddleDashboardUrl, row.paddleTransactionId)}><span className="sr-only">Open in Paddle</span><ExternalLink size={13} /></Button>}{row.invoicePdfAvailable && <a href={`/api/admin/transactions/${row.paddleTransactionId}/invoice-pdf`} target="_blank" rel="noreferrer"><Button variant="quiet" title="Open invoice PDF"><FileDown size={13} /></Button></a>}</div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {transactionsQ.hasNextPage && <div className="flex justify-center border-t border-border p-3"><Button disabled={transactionsQ.isFetchingNextPage} onClick={() => transactionsQ.fetchNextPage()}>{transactionsQ.isFetchingNextPage ? "Loading…" : "Load 50 more"}</Button></div>}
        {transactionsQ.isFetchNextPageError && <div className="border-t border-border p-3"><Callout tone="danger" title="The next page could not be loaded">{errorMessage(transactionsQ.error)}</Callout></div>}
      </Panel>
    </div>
  );
}

function openPaddleTransaction(dashboardUrl: string, transactionId: string) {
  window.open(dashboardUrl, "_blank", "noopener,noreferrer");
  void navigator.clipboard.writeText(transactionId);
}

function totalsByCurrency(rows: TransactionRow[]) {
  const totals = new Map<string, number>();
  for (const row of rows) totals.set(row.currency, (totals.get(row.currency) ?? 0) + row.totalMinor);
  return [...totals.entries()].map(([currency, amount]) => formatMoney(amount, currency)).join(" · ");
}
